import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Card, CardBody, CardFooter, CardHeader, Col, CustomInput, FormGroup, Input, Label, Row } from "reactstrap";
import { useAccessory } from "../../context/AccessoryContext";
import AccessoryProgress from "./AccessoryProgress";
import { useService } from "../../context/ServiceContext";
import ProductInfo from "../ProductCreation/ProductInfo";
import { fireAlert } from "../../utils/alerts";
import LoadedContent from "../LoadedContent";
import { ValidationForm } from "../ValidationForm";
import { ErrorSummary } from "./AccessorySummary";

function NewAccessorySeries({ onNext, progressItems, history }) {
  const { newAccessory, accessory, updateAccessory, getErrors, invalidateSession } = useAccessory();
  const { manufacturerCompaniesProvider, seriesExistenceProvider, accessorySpecificationStore } = useService();
  const intl = useIntl();
  const series = accessory.series;
  const [saving, setSaving] = useState(false);
  const [seriesExists, setSeriesExists] = useState(false);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    async function getData() {
      try {
        const companies = await manufacturerCompaniesProvider.get();
        setCompanies(companies);
      } catch (error) {
      } finally {
      }
    }
    getData();
    if (accessory == null || Object.keys(accessory).length == 0) {
      newAccessory({ id: -1, activo: true, flagProduction: 1, flagObsoleto: 0, idCompanyManufacturer: "00000" });
    }
  }, []);

  async function lookupSeries() {
    const serieComercial = accessory.series.name;
    const result = await seriesExistenceProvider.get(serieComercial);
    if (result?.exists) {
      if (seriesExists != serieComercial) {
        fireAlert(
          intl.formatMessage({ id: "accessory_information.series_already_exists", defaultMessage: "Series already exists" }),
          intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
          "error"
        );
      }
      setSeriesExists(serieComercial);
      return;
    }
    setSeriesExists("");
  }

  async function validate(e) {
    e.preventDefault();

    const errors = await getErrors(accessory, true);
    if (!errors.hasErrors) {
      onNext();
    } else {
      fireAlert(<ErrorSummary intl={intl} errors={errors}></ErrorSummary>, "Incorrect attributes", "error");
    }
  }

  function updateSeries(property, value) {
    if (accessory.id == undefined) {
      newAccessory({ id: -1, [property]: value });
    } else {
      updateAccessory({ ...accessory, series: { ...accessory.series, [property]: value } });
    }
  }

  async function saveSeriesOnly() {
    setSaving(true);

    try {
      const response = await accessorySpecificationStore.store([{ ...accessory, seriesOnly: true }]);

      if (response.result == false) {
        fireAlert("Something went wrong", intl.formatMessage({ id: "app.error", defaultMessage: "Error" }), "error");
      } else {
        updateAccessory({ ...accessory }, true);
        fireAlert(
          intl.formatMessage({ id: "accessory_summary.accessory_series_saved", defaultMessage: "Accessory series successfully saved." }),
          intl.formatMessage({ id: "app.success", defaultMessage: "Success" }),
          "success"
        ).then(() => {
          invalidateSession();
          history.push("/accessories");
        });
      }
    } catch (error) {
      fireAlert("Something went wrong", intl.formatMessage({ id: "app.error", defaultMessage: "Error" }), "error");
    }

    setSaving(false);
  }

  return (
    <ValidationForm onSubmit={validate}>
      <Row>
        <Col xl={3} className="d-sm-none d-xl-block">
          <AccessoryProgress onNext={() => {}} items={progressItems}></AccessoryProgress>
        </Col>
        <Col xl={9}>
          <LoadedContent loading={saving} overlay={true}>
            <Card>
              <CardHeader>
                <FormattedMessage id="accessory_information.title" defaultMessage="Please enter the accessory information" />
              </CardHeader>
              <CardBody className="p-3">
                <FormGroup row className="px-4">
                  <Label sm={3}>
                    <FormattedMessage id="attribute.idCompanyManufacturer" defaultMessage="Manufacturing Company" />
                    <ProductInfo formattedMessage={{ id: "attribute_info.idCompanyManufacturer", defaultMessage: "attribute_info.idCompanyManufacturer" }}></ProductInfo>
                  </Label>
                  <Col sm={9}>
                    <Input
                      type="select"
                      id="idCompanyManufacturer"
                      value={accessory.series?.idCompanyManufacturer || ""}
                      onChange={(e) => updateSeries("idCompanyManufacturer", e.target.value)}
                    >
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.value}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </FormGroup>
                <FormGroup row className="px-4">
                  <Label sm={3}>
                    <FormattedMessage id="attribute.active" defaultMessage="Active" />
                    <ProductInfo formattedMessage={{ id: "attribute_info.active", defaultMessage: "attribute_info.active" }}></ProductInfo>
                  </Label>
                  <Col sm={3}>
                    <CustomInput
                      type="switch"
                      id={"activo"}
                      checked={accessory.series?.activo ?? true}
                      onChange={(e) => {
                        //setActivo(e.target.checked);
                        updateSeries("activo", e.target.checked);
                      }}
                    />
                  </Col>
                  <Label sm={3}>
                    <FormattedMessage id="attribute.flagProduction" defaultMessage="Blocked for Production" />
                    <ProductInfo
                      formattedMessage={{ id: "attribute_info.flagProduction", defaultMessage: "Select this option if this product should not be visible on EasyVent production." }}
                    ></ProductInfo>
                  </Label>
                  <Col sm={3}>
                    <CustomInput
                      type="switch"
                      id={"flagProduction"}
                      checked={!(accessory.series?.flagProduction ?? true)}
                      onChange={(e) => {
                        //setFlagProduction(e.target.checked ? 0 : 1);
                        updateSeries("flagProduction", e.target.checked ? 0 : 1);
                      }}
                    />
                  </Col>
                </FormGroup>
                <FormGroup row className="px-4">
                  <Label sm={3}>
                    <FormattedMessage id="attribute.flagObsoleto" defaultMessage="Obsoleto" />
                    <ProductInfo formattedMessage={{ id: "attribute_info.flagObsoleto", defaultMessage: "attribute_info.flagObsoleto" }}></ProductInfo>
                  </Label>
                  <Col sm={3}>
                    <CustomInput
                      type="switch"
                      id={"flagObsoleto"}
                      checked={accessory.series?.flagObsoleto == 1 ?? 0}
                      onChange={(e) => {
                        //setFlagObsoleto(e.target.checked ? 1 : 0);
                        updateSeries("flagObsoleto", e.target.checked ? 1 : 0);
                      }}
                    />
                  </Col>
                </FormGroup>

                <FormGroup row className="px-4">
                  <Label sm={3}>
                    <FormattedMessage id="attribute.internalSeriesName" defaultMessage="Internal series name" />
                    <ProductInfo formattedMessage={{ id: "attribute_info.internalSeriesName", defaultMessage: "attribute_info.internalSeriesName" }}></ProductInfo>
                  </Label>
                  <Col sm={3}>
                    <Input
                      type="text"
                      id={"serieComercial"}
                      name={"serieComercial"}
                      className={`flex-grow-1 form-control ${seriesExists ? "is-invalid" : ""}`}
                      required={true}
                      value={accessory.series?.name ?? ""}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^a-zA-Z0-9_\/\\-]/g, "");
                        updateSeries("name", val);
                      }}
                      onBlur={lookupSeries}
                    />
                  </Col>

                  <Label sm={3}>
                    <FormattedMessage id="attribute.visibleSeriesName" defaultMessage="Visible series name" />
                    <ProductInfo formattedMessage={{ id: "attribute_info.visibleSeriesName", defaultMessage: "attribute_info.visibleSeriesName" }}></ProductInfo>
                  </Label>
                  <Col sm={3}>
                    <Input
                      type="text"
                      id="shortNameComercial"
                      name="shortNameComercial"
                      className="flex-grow-1"
                      required={true}
                      value={accessory.series?.shortNameComercial ?? ""}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^a-zA-Z0-9_\/\\ -]/g, "");
                        updateSeries("shortNameComercial", val);
                      }}
                    />
                  </Col>
                </FormGroup>
              </CardBody>
              <CardFooter>
                <button className="btn btn-outline-danger float-right" type="button" onClick={saveSeriesOnly}>
                  <FormattedMessage id="new_accessory_series.finish_and_save" defaultMessage="Finish and save series" />
                </button>
              </CardFooter>
            </Card>
          </LoadedContent>
        </Col>
      </Row>
    </ValidationForm>
  );
}

export default NewAccessorySeries;
